import { AppRoutes } from "@/shared/lib/routes"
import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { createRoute } from "@tanstack/react-router"
import { useEffect } from "react"
import { rootRoute } from "./__root"
import { useNavigateWithParams } from "@/shared/hooks/use-navigate-with-params"
import { SignInButtons, SignInHero, SignInLoader, useSignIn } from "@/features/auth"
import { finalizeSubscriptionForUser } from "@/features/payments"
import { syncUserProfileFromQuiz } from "@/features/user"
import { useSubscriptionStore } from "@/stores/subscription-store"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { MobileLayout } from "@/shared/components/mobile-layout"

interface ActivateSearch {
  /** The subscription id carried by the emailed activation link (`?id=...`). */
  id: string
}

export const activateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AppRoutes.ACTIVATE,
  validateSearch: (raw: Record<string, unknown>): ActivateSearch => ({
    id: typeof raw.id === "string" ? raw.id : "",
  }),
  component: ActivatePage,
})

/**
 * Emailed account-activation link (`/activate?id={subscriptionId}`).
 *
 * When a user buys but never signs in, the subscription is never bound to an
 * account (finalize runs client-side at `/create-account`, which needs the
 * purchase kept in the store on that device). The purchase email links here so
 * they can finish later, even on another device: sign in, and the subscription
 * id from the URL is finalized and bound to the account before entering the app.
 */
function ActivatePage() {
  const navigate = useNavigateWithParams()
  const { id } = activateRoute.useSearch()
  const { status: authStatus, user, handleApple, handleGoogle } = useSignIn()
  const grantLocalAccess = useSubscriptionStore((s) => s.grantLocalAccess)

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.SIGN_IN_VIEWED)
  }, [])

  useEffect(() => {
    if (authStatus !== "authenticated" || !user) return

    let cancelled = false

    ;(async () => {
      // Bind the just-signed-in account to the subscription from the link.
      try {
        await finalizeSubscriptionForUser(user, { subscriptionId: id })
      } catch (err) {
        console.error("activate finalize failed", err)
      }
      // Demo/no-backend fallback (ignored once a backend verifies access).
      grantLocalAccess()

      // Seed/refresh the backend profile if quiz data is present on this device
      // (no-ops on a fresh device). Non-blocking: never gate entry on it.
      try {
        await syncUserProfileFromQuiz()
      } catch (err) {
        console.error("user profile sync failed", err)
      }

      if (cancelled) return
      trackEvent(ANALYTICS_EVENTS.SIGN_UP, { userId: user.uid })
      navigate({ to: WebappRoutes.ADVISORS })
    })()

    return () => {
      cancelled = true
    }
  }, [authStatus, user, id, navigate, grantLocalAccess])

  // While Firebase resolves the session (incl. returning from a redirect) and
  // while we finalize, show a loader instead of flashing the sign-in buttons.
  if (authStatus === "loading" || authStatus === "authenticated") {
    return <SignInLoader />
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-dark-base font-body text-purple-light">
      <MobileLayout className="flex flex-1 flex-col items-center justify-center gap-6 py-12">
        <SignInHero />

        <div className="space-y-2 text-center">
          <h1 className="font-display text-2xl font-medium leading-tight text-purple-light">
            Activate Your Account
          </h1>
          <p className="text-base text-purple-light/70">
            Sign in to activate your subscription and open your reports and personal insights
          </p>
        </div>

        <SignInButtons onApple={handleApple} onGoogle={handleGoogle} />
      </MobileLayout>
    </div>
  )
}
